import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { getLiveWatchedPaths } from '@framework/validation/live/live.ts';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { type IMaterializedReference } from '../../evaluate/materializeReferencePath.ts';

/**
 * Restituisce il validation context corretto per le context-refs fornite.
 *
 * Se nessuna ref legge da `$semanticFields.*` oppure lo schema non ha path
 * live associati, restituisce il context statico di `FormAugmentedContext`.
 *
 * Altrimenti sottoscrive ai path live per forzare il re-render a ogni
 * keystroke sui campi che alimentano i semantic fields, e ricalcola il
 * context via `resolvedStore.getContext()` per ottenere i semantic fields
 * aggiornati al valore corrente del form.
 *
 * @param contextRefs Sole le context-refs (path che iniziano con `$`).
 *   Passare l'array già partizionato da `partitionReferences` per evitare
 *   re-calcoli inutili.
 */
export function useLiveValidationContext(
  contextRefs: IMaterializedReference[],
): any {
  const { schema, validationContext, resolvedStore } =
    useFormAugmentedContext();
  const { control, getValues } = useFormContext();

  const hasSemanticFieldRefs = useMemo(
    () =>
      contextRefs.some(ref => ref.absolutePath.startsWith('$semanticFields.')),
    [contextRefs],
  );

  const liveFieldPaths = useMemo(() => getLiveWatchedPaths(schema), [schema]);
  const needsLiveContext = hasSemanticFieldRefs && liveFieldPaths.length > 0;

  // La sottoscrizione non serve per il valore: serve solo per forzare il
  // re-render quando cambiano i campi che alimentano i semantic fields live.
  const liveWatchedValues = useWatch({
    control,
    name: liveFieldPaths,
    disabled: !needsLiveContext,
  });

  return useMemo(() => {
    if (!needsLiveContext) return validationContext;
    return resolvedStore.getContext(getValues());
  }, [
    needsLiveContext,
    liveWatchedValues,
    resolvedStore,
    validationContext,
    getValues,
  ]);
}
